import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { Alert, Card, Collapse, Empty, Space, Spin, Tree, Typography, Button, Tag } from 'antd'
import { personLabel, StatusBadge } from './HRDashboard'
import AppraisalPdfButton from './AppraisalPdfButton'
import { useI18n } from '../i18n/I18nProvider'
import CompletedAppraisalRatings from './CompletedAppraisalRatings'

const { Text } = Typography
const INDIRECT_DELEGATION = 'functional_review'

function delegationPersonLabel(name, email) {
  const displayName = (name || '').trim()
  const displayEmail = (email || '').trim()
  return displayName || displayEmail || '—'
}

function requestedReviews(appraisal) {
  if (appraisal.requested_functional_reviews?.length) return appraisal.requested_functional_reviews
  if (!appraisal.requested_functional_review_id) return []
  return [{
    id: appraisal.requested_functional_review_id,
    reviewer_name: appraisal.requested_functional_review_reviewer_name,
    reviewer_email: appraisal.requested_functional_review_reviewer_email,
    status: appraisal.requested_functional_review_status
  }]
}

function indirectDelegationRequests(appraisal) {
  return (appraisal.delegation_request_log || []).filter(
    request => request.delegation_mode === INDIRECT_DELEGATION
  )
}

function unassignedReviewerRequests(appraisal) {
  const assignedReviewerEmails = new Set(
    requestedReviews(appraisal)
      .map(review => review.reviewer_email?.trim().toLowerCase())
      .filter(Boolean)
  )
  const seenReviewerEmails = new Set()

  return indirectDelegationRequests(appraisal).filter(request => {
    const email = request.proposed_manager_email?.trim().toLowerCase()
    if (!email) return true
    if (assignedReviewerEmails.has(email) || seenReviewerEmails.has(email)) return false
    seenReviewerEmails.add(email)
    return true
  })
}

function delegationStatusMeta(status) {
  const value = String(status || '').toLowerCase()
  if (value === 'rejected') return { label: 'Rejected', color: 'red' }
  if (value === 'pending') return { label: 'Pending', color: 'orange' }
  return { label: 'Completed', color: 'green' }
}

function feedbackTabRoute(appraisalId, reviewId) {
  if (!reviewId) return `/appraisals/${appraisalId}/manager`
  return `/appraisals/${appraisalId}/manager/feedback/${reviewId}`
}

function delegatedAppraisalRouteFor(row) {
  const reviews = requestedReviews(row)
  const prioritizedReview = reviews.find(review => review.status === 'submitted') || reviews[0]
  if (prioritizedReview?.id) {
    return feedbackTabRoute(row.id, prioritizedReview.id)
  }
  if (row.latest_delegation_to_manager_id || row.current_manager_access || row.active_functional_review_id) {
    if (row.active_functional_review_id) {
      return feedbackTabRoute(row.id, row.active_functional_review_id)
    }
    return `/appraisals/${row.id}/manager`
  }
  return `/appraisals/${row.id}`
}

function DelegationStatusTree({ row }) {
  const reviews = requestedReviews(row)
  const requests = unassignedReviewerRequests(row)
  const delegatedFromLabel = row.latest_delegation_from_manager_name || row.latest_delegation_from_manager_email

  return (
    <div style={{ width: '100%' }}>
      <div style={{ marginBottom: (reviews.length || requests.length || delegatedFromLabel) ? 6 : 0 }}>
        <StatusBadge status={row.status} label={row.status_label} />
      </div>
      {(reviews.length > 0 || requests.length > 0 || delegatedFromLabel) && (
        <div style={{ marginLeft: 6, borderLeft: '1px solid #d9d9d9', paddingLeft: 12 }}>
          {reviews.map(review => (
            <Space key={review.id} size={[4, 4]} wrap>
              <Text type='secondary'>↳</Text>
              <Tag color='purple'>Requested Feedback from: {delegationPersonLabel(review.reviewer_name, review.reviewer_email)}</Tag>
              {(() => {
                const { label, color } = delegationStatusMeta(review.status)
                return <Tag color={color}>{label}</Tag>
              })()}
            </Space>
          ))}
          {requests.map(request => (
            <Space key={`request-${request.id}`} size={[4, 4]} wrap>
              <Text type='secondary'>↳</Text>
              <Tag color='blue'>
                Indirect delegation: {request.proposed_manager_name
                  ? delegationPersonLabel(request.proposed_manager_name, request.proposed_manager_email)
                  : 'HR to assign'}
              </Tag>
              {(() => {
                const { label, color } = delegationStatusMeta(request.status)
                return <Tag color={color}>{label}</Tag>
              })()}
            </Space>
          ))}
          {delegatedFromLabel && (
            <Space size={[4, 4]} wrap>
              <Text type='secondary'>↳</Text>
              <Tag color='blue'>Original Manager: {delegatedFromLabel}</Tag>
            </Space>
          )}
        </div>
      )}
    </div>
  )
}

function uniqueById(rows) {
  const seen = new Set()
  const out = []
  for (const row of rows || []) {
    if (!row || seen.has(row.id)) continue
    seen.add(row.id)
    out.push(row)
  }
  return out
}

function managerLabel(row, t) {
  return personLabel(row.manager_first_name, row.manager_last_name, row.manager_email, row.manager_position, row.session_template) || t('reports.manager_number', 'Manager #{{id}}', { id: row.manager })
}

function buildTreeForSession(rows, currentUserId, t) {
  const byManager = new Map()
  const byEmployee = new Map()

  for (const row of rows) {
    byEmployee.set(row.employee, row)
    const key = row.manager || 'unassigned'
    if (!byManager.has(key)) byManager.set(key, [])
    byManager.get(key).push(row)
  }

  const buildEmployeeNode = (row, seen = new Set()) => {
    const nodeKey = `emp-${row.employee}-${row.id}`
    if (seen.has(nodeKey)) return null
    const nextSeen = new Set(seen)
    nextSeen.add(nodeKey)

    const children = (byManager.get(row.employee) || [])
      .map(child => buildEmployeeNode(child, nextSeen))
      .filter(Boolean)

    return {
      key: nodeKey,
      title: (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <Space size='small' wrap>
            <Text>{personLabel(row.employee_first_name, row.employee_last_name, row.employee_email, row.employee_position, row.session_template)}</Text>
            <Text type='secondary'>{t('appraisals.table.employee_id', 'Employee ID')}: {row.employee_company_id || '—'}</Text>
            <CompletedAppraisalRatings appraisal={row} />
            {row.current_manager_access && row.manager !== currentUserId && (
              <>
                <Tag color='blue'>{t('reports.current_manager_access', 'Current manager access')}</Tag>
                <Text type='secondary'>{t('reports.manager_at_time', 'Manager at time: {{manager}}', { manager: managerLabel(row, t) })}</Text>
              </>
            )}
            <Link to={delegatedAppraisalRouteFor(row)}><Button size='small' type='link'>{t('common.view', 'View')}</Button></Link>
            <AppraisalPdfButton appraisalId={row.id} status={row.status} canExport={row.can_export_pdf} type='link' />
          </Space>
          <DelegationStatusTree row={row} />
        </div>
      ),
      children
    }
  }

  const managerIds = [...new Set(rows.map(r => r.manager).filter(Boolean))]
  const rootManagerIds = managerIds.filter(mid => {
    const managerRow = byEmployee.get(mid)
    return !managerRow || !managerRow.manager || !managerIds.includes(managerRow.manager)
  })

  const managerNodes = rootManagerIds.map(mid => {
    const direct = byManager.get(mid) || []
    if (direct.length === 0) return null
    return {
      key: `mgr-${mid}`,
      title: <Text strong>{managerLabel(direct[0], t)}</Text>,
      children: direct.map(row => buildEmployeeNode(row)).filter(Boolean)
    }
  }).filter(Boolean)

  const unassigned = (byManager.get('unassigned') || []).map(row => buildEmployeeNode(row)).filter(Boolean)
  if (unassigned.length > 0) {
    managerNodes.push({
      key: 'mgr-unassigned',
      title: <Text strong>{t('reports.unassigned_manager', 'Unassigned Manager')}</Text>,
      children: unassigned
    })
  }

  return managerNodes
}

export default function ReportHistorySection({ appraisals = [], sessions = [], title, excludeEmployeeId, currentUserId, loadAppraisalsForSession, appraisalFilter }) {
  const { t } = useI18n()
  const resolvedTitle = title || t('reports.past_appraisals', 'Past Report Appraisals')
  const [loadedRowsBySession, setLoadedRowsBySession] = useState({})
  const [loadingSessionIds, setLoadingSessionIds] = useState({})
  const [sessionErrors, setSessionErrors] = useState({})
  const scoped = uniqueById(appraisals).filter(a => a.employee !== excludeEmployeeId)
  const past = appraisalFilter
    ? scoped.filter(appraisalFilter)
    : scoped.filter(a => a.session_status === 'closed')
  const lazySessions = useMemo(() => sessions.filter(session => session.status === 'closed'), [sessions])
  const isLazy = Boolean(loadAppraisalsForSession)

  if (past.length === 0 && lazySessions.length === 0) {
    return (
      <Card size='small' title={resolvedTitle}>
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('reports.empty', 'No past report appraisals found.')} />
      </Card>
    )
  }

  const groups = past.reduce((acc, row) => {
    const key = `${row.session_name}__${row.session_end_date || ''}`
    if (!acc[key]) {
      acc[key] = {
        key,
        sessionId: row.session,
        sessionName: row.session_name,
        sessionEndDate: row.session_end_date,
        rows: []
      }
    }
    acc[key].rows.push(row)
    return acc
  }, {})

  for (const session of lazySessions) {
    const key = `session-${session.id}`
    if (!groups[key]) {
      groups[key] = {
        key,
        sessionId: session.id,
        sessionName: session.name,
        sessionEndDate: session.end_date,
        rows: loadedRowsBySession[session.id] || []
      }
    }
  }

  async function loadSessionRows(sessionId) {
    if (!loadAppraisalsForSession || !sessionId || loadedRowsBySession[sessionId] || loadingSessionIds[sessionId]) return
    setLoadingSessionIds(prev => ({ ...prev, [sessionId]: true }))
    setSessionErrors(prev => {
      const next = { ...prev }
      delete next[sessionId]
      return next
    })
    try {
      const rows = await loadAppraisalsForSession(sessionId)
      setLoadedRowsBySession(prev => ({ ...prev, [sessionId]: uniqueById(rows).filter(a => a.employee !== excludeEmployeeId) }))
    } catch (e) {
      setSessionErrors(prev => ({ ...prev, [sessionId]: e.message || t('reports.messages.load_failed', 'Unable to load report appraisals.') }))
    } finally {
      setLoadingSessionIds(prev => ({ ...prev, [sessionId]: false }))
    }
  }

  function onCollapseChange(keys) {
    const activeKeys = Array.isArray(keys) ? keys : [keys]
    activeKeys.forEach(key => {
      const group = groups[key]
      if (group?.sessionId) loadSessionRows(group.sessionId)
    })
  }

  const panels = Object.values(groups)
    .sort((a, b) => String(b.sessionEndDate || '').localeCompare(String(a.sessionEndDate || '')))
    .map(g => {
      const rows = loadedRowsBySession[g.sessionId] || g.rows
      const treeData = buildTreeForSession(rows, currentUserId, t)
      const labelCount = rows.length
        ? ` (${rows.length === 1
          ? t('reports.appraisal_count_one', '{{count}} appraisal', { count: rows.length })
          : t('reports.appraisal_count_many', '{{count}} appraisals', { count: rows.length })})`
        : ''
      return {
        key: g.key,
        label: `${g.sessionName}${labelCount}`,
        children: loadingSessionIds[g.sessionId]
          ? <div style={{ textAlign: 'center', padding: 24 }}><Spin /></div>
          : sessionErrors[g.sessionId]
            ? <Alert type='error' showIcon message={sessionErrors[g.sessionId]} />
            : treeData.length > 0
              ? <Tree showLine defaultExpandAll treeData={treeData} />
              : <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('reports.no_hierarchy', 'No manager hierarchy available for this session.')} />
      }
    })

  return (
    <Card size='small' title={resolvedTitle}>
      <Collapse items={panels} defaultActiveKey={!isLazy && panels[0] ? [panels[0].key] : []} onChange={onCollapseChange} />
    </Card>
  )
}
